// Handovers to partners (12 §12.5): the state machine of a handover, who may take which unit, the
// counts that follow from the units, and when a handover completes by itself.
// Pure: the server reads the documents and calls these; nothing here knows Firestore.
import type { Box } from './box-types';
import { hasActiveHold } from './box-types';

export const HANDOVER_STATUSES = [
  'draft',
  'loading',
  'handed_over',
  'in_progress',
  'completed',
  'closed',
] as const;
export type HandoverStatus = (typeof HANDOVER_STATUSES)[number];

export const HANDOVER_ACTIONS = [
  'load',
  'remove',
  'handOver',
  'update',
  'complete',
  'close',
] as const;
export type HandoverAction = (typeof HANDOVER_ACTIONS)[number];

/** One transaction has to move, accrue and notify every unit of a handover (03 §3.7 fan-out is not built). */
export const MAX_UNITS_PER_HANDOVER = 400;
/** A partner's link keeps working this long after the handover completes or closes (12 §12.7). */
export const LINK_DAYS_AFTER_CLOSE = 30;

interface Row {
  readonly from: HandoverStatus;
  readonly action: HandoverAction;
  readonly to: HandoverStatus;
}

/** `draft → loading → handed_over → in_progress → completed → closed`, with the manual shortcuts. */
export const HANDOVER_TRANSITIONS: readonly Row[] = [
  { from: 'draft', action: 'load', to: 'loading' },
  { from: 'loading', action: 'load', to: 'loading' },
  { from: 'loading', action: 'remove', to: 'loading' },
  { from: 'loading', action: 'handOver', to: 'handed_over' },
  { from: 'handed_over', action: 'update', to: 'in_progress' },
  { from: 'in_progress', action: 'update', to: 'in_progress' },
  { from: 'handed_over', action: 'complete', to: 'completed' },
  { from: 'in_progress', action: 'complete', to: 'completed' },
  { from: 'draft', action: 'close', to: 'closed' },
  { from: 'loading', action: 'close', to: 'closed' },
  { from: 'handed_over', action: 'close', to: 'closed' },
  { from: 'in_progress', action: 'close', to: 'closed' },
  { from: 'completed', action: 'close', to: 'closed' },
];

/** The status after the action, or `undefined` when the handover's status does not allow it. */
export function handoverTransition(
  from: HandoverStatus,
  action: HandoverAction,
): HandoverStatus | undefined {
  return HANDOVER_TRANSITIONS.find((r) => r.from === from && r.action === action)?.to;
}

export function legalHandoverActions(from: HandoverStatus): HandoverAction[] {
  return [...new Set(HANDOVER_TRANSITIONS.filter((r) => r.from === from).map((r) => r.action))];
}

/** Statuses in which units are only listed, not yet with the partner. */
export const isStaging = (status: HandoverStatus): boolean =>
  status === 'draft' || status === 'loading';
/** Statuses in which the partner holds units and may send updates. */
export const isWithPartner = (status: HandoverStatus): boolean =>
  status === 'handed_over' || status === 'in_progress';

// ------------------------------------------------------------------ who may go

export type PartnerKind = 'hauler' | 'courier';

/** Which kind of handover a partner type takes: haulers deliver sea and air cargo, couriers carry Prime. */
export function partnerKindOf(partnerType: string): PartnerKind | undefined {
  if (partnerType === 'local_hauler') return 'hauler';
  if (partnerType === 'courier_forwarder') return 'courier';
  return undefined;
}

/** A hauler takes boxes of sea and air shipments; a courier takes Prime. */
export function serviceFitsKind(service: string, kind: PartnerKind): boolean {
  return kind === 'courier' ? service === 'prime' : service !== 'prime';
}

export type UnitRefusalCode =
  | 'box_final'
  | 'already_with_partner'
  | 'not_here'
  | 'unpaid'
  | 'on_hold'
  | 'wrong_partner_type'
  | 'wrong_partner'
  | 'own_delivery_city'
  | 'no_coverage'
  | 'already_in_handover';

export interface UnitRefusal {
  readonly code: UnitRefusalCode;
  readonly message: string;
}

export interface UnitCheckInput {
  readonly box: Box;
  /** The unit's label for messages (barcode or piece label). */
  readonly unitId: string;
  readonly handover: {
    readonly id: string;
    readonly branchId: string;
    readonly partnerId: string;
    readonly kind: PartnerKind;
  };
  readonly shipment: {
    readonly service: string;
    readonly paidInFull: boolean;
    readonly balanceText?: string | undefined;
  };
  /** The destination city's coverage, for hauler handovers. */
  readonly coverage?:
    | {
        readonly mode: 'own' | 'partner';
        readonly partnerId?: string | undefined;
        readonly backupPartnerId?: string | undefined;
      }
    | undefined;
  /** The city the shipment goes to, for messages. */
  readonly cityName?: string | undefined;
  /** The handover this unit is already staged in, if any. */
  readonly stagedHandoverId?: string | undefined;
}

/**
 * The business rules for putting a unit on a handover (12 §12.5, D-16), in the order a person
 * would explain them. `undefined` means the unit may go. The same rules run when units are loaded
 * and again when the partner signs, because a payment can be refunded or a hold placed in between.
 */
export function checkUnitForHandover(i: UnitCheckInput): UnitRefusal | undefined {
  const { box, unitId, handover } = i;
  if (box.status === 'with_partner') {
    return {
      code: 'already_with_partner',
      message: `${unitId} is already with a partner (handover ${box.partner?.handoverId ?? 'unknown'}). A unit can be with one partner at a time.`,
    };
  }
  if (box.status !== 'at_branch') {
    return {
      code:
        box.status === 'delivered' ||
        box.status === 'lost' ||
        box.status === 'cancelled' ||
        box.status === 'returned'
          ? 'box_final'
          : 'not_here',
      message: `${unitId} is ${box.status.replace(/_/g, ' ')}, so it cannot be handed over.`,
    };
  }
  if (box.currentBranchId !== handover.branchId) {
    return {
      code: 'not_here',
      message: `${unitId} is at ${box.currentBranchId}, not at ${handover.branchId} where this handover is. Receive it here first.`,
    };
  }
  if (i.stagedHandoverId !== undefined && i.stagedHandoverId !== handover.id) {
    return {
      code: 'already_in_handover',
      message: `${unitId} is already listed on handover ${i.stagedHandoverId}. Take it off that one first.`,
    };
  }
  if (!i.shipment.paidInFull) {
    return {
      code: 'unpaid',
      message: `${unitId} (shipment ${box.shipmentId}) is not paid in full${i.shipment.balanceText ? `: ${i.shipment.balanceText} is still to pay` : ''}. It cannot be handed over until it is.`,
    };
  }
  if (hasActiveHold(box)) {
    const holds = box.holds.filter((h) => h.releasedAt === undefined).map((h) => h.type);
    return {
      code: 'on_hold',
      message: `${unitId} (shipment ${box.shipmentId}) has a hold (${holds.join(', ')}). Release it first.`,
    };
  }
  if (!serviceFitsKind(i.shipment.service, handover.kind)) {
    return {
      code: 'wrong_partner_type',
      message:
        handover.kind === 'courier'
          ? `${unitId} is on a ${i.shipment.service} shipment. Only Prime goes to a courier.`
          : `${unitId} is a Prime parcel. Prime goes to a courier, not to a hauler.`,
    };
  }
  if (handover.kind === 'hauler') {
    if (i.coverage === undefined) {
      return {
        code: 'no_coverage',
        message: `${i.cityName ?? 'The destination city'} has no coverage set up, so it is not known which hauler delivers it.`,
      };
    }
    if (i.coverage.mode === 'own') {
      return {
        code: 'own_delivery_city',
        message: `${i.cityName ?? 'This city'} is delivered by EcoBox itself, not by a hauler.`,
      };
    }
    const allowed = [i.coverage.partnerId, i.coverage.backupPartnerId];
    if (!allowed.includes(handover.partnerId)) {
      return {
        code: 'wrong_partner',
        message: `${i.cityName ?? 'This city'} is assigned to another hauler. Change the coverage first if this partner should take it.`,
      };
    }
  }
  return undefined;
}

// ------------------------------------------------------------------- counts

export interface HandoverItemFacts {
  readonly partnerStatus?: string | undefined;
  readonly settled?: 'delivered' | 'returned' | 'written_off' | undefined;
}

export interface HandoverCounts {
  units: number;
  delivered: number;
  open: number;
  problems: number;
  returned: number;
  writtenOff: number;
}

const PROBLEM_STATUSES: ReadonlySet<string> = new Set(['failed_attempt', 'damaged', 'lost']);

/** What the units say about the handover. A unit is open until it is delivered, returned or written off. */
export function handoverCounts(items: readonly HandoverItemFacts[]): HandoverCounts {
  const counts: HandoverCounts = {
    units: items.length,
    delivered: 0,
    open: 0,
    problems: 0,
    returned: 0,
    writtenOff: 0,
  };
  for (const item of items) {
    if (item.settled === 'delivered') counts.delivered++;
    else if (item.settled === 'returned') counts.returned++;
    else if (item.settled === 'written_off') counts.writtenOff++;
    else {
      counts.open++;
      if (item.partnerStatus !== undefined && PROBLEM_STATUSES.has(item.partnerStatus)) {
        counts.problems++;
      }
    }
  }
  return counts;
}

/** A handover completes by itself when every unit is delivered, returned or written off (12 §12.5 step 7). */
export function isHandoverComplete(counts: Pick<HandoverCounts, 'units' | 'open'>): boolean {
  return counts.units > 0 && counts.open === 0;
}

export type CloseVerdict = { ok: true } | { ok: false; reason: 'already_closed' | 'units_open' };

/**
 * A handover closes by hand only when nothing is still out with the partner. While a unit is open
 * it has to be delivered, returned or written off first, so the books and the stock stay true.
 */
export function canCloseHandover(
  status: HandoverStatus,
  counts: Pick<HandoverCounts, 'open'>,
): CloseVerdict {
  if (status === 'closed') return { ok: false, reason: 'already_closed' };
  if (isWithPartner(status) && counts.open > 0) return { ok: false, reason: 'units_open' };
  return { ok: true };
}

/** When a link stops working: 30 days after the handover completed or closed. */
export function linkExpiryAfter(closedAt: Date | string): string {
  const at = typeof closedAt === 'string' ? new Date(closedAt) : closedAt;
  return new Date(at.getTime() + LINK_DAYS_AFTER_CLOSE * 86_400_000).toISOString();
}

/** The first moment the partner owes the unit: the handover plus the city's target days. */
export function unitDueAt(handedOverAt: Date | string, targetDays: number): string {
  const at = typeof handedOverAt === 'string' ? new Date(handedOverAt) : handedOverAt;
  return new Date(at.getTime() + targetDays * 86_400_000).toISOString();
}
